import { refreshIdToken } from '../auth-refresh';
import { getValidIdToken } from '../cookie-auth';
import { gridRowToProcessPayload } from '../helpers/staffImportGridMapping';
import type {
    AnalyzeResult,
    GeneratedImportJobSummary,
    ImportJobStatus,
    LatestGeneratedImportJobResult,
    ProcessResult,
    StaffImportRow,
} from '../types/staffImport';

declare const __STAFF_IMPORT_API_URL__: string;

type ApiJson = Record<string, unknown>;

const getStaffImportApiUrl = (): string => {
    if (typeof __STAFF_IMPORT_API_URL__ !== 'undefined') {
        return __STAFF_IMPORT_API_URL__;
    }
    return '';
};

const parseJsonResponse = async (response: Response): Promise<ApiJson> => {
    const payload = (await response.json()) as ApiJson;
    if (!response.ok) {
        const structuredError = payload.error as ApiJson | undefined;
        const errorMessage =
            typeof payload.error === 'string'
                ? payload.error
                : typeof structuredError?.message === 'string'
                  ? structuredError.message
                  : `Request failed with status ${response.status}.`;
        const error = new Error(errorMessage) as Error & {
            code?: string;
            retryable?: boolean;
        };
        error.code =
            typeof structuredError?.code === 'string'
                ? structuredError.code
                : undefined;
        error.retryable = structuredError?.retryable === true;
        throw error;
    }
    return payload;
};

export const getAuthHeaders = async (): Promise<Record<string, string>> => {
    const token = await getValidIdToken(refreshIdToken);
    if (!token) {
        return {};
    }
    return { Authorization: `Bearer ${token}` };
};

const mapEtlRowToStaffImportRow = (row: ApiJson): StaffImportRow => ({
    firstName: String(row.first_name ?? ''),
    middleName: String(row.middle_name ?? ''),
    lastName: String(row.last_name ?? ''),
    role: String(row.role ?? ''),
    email: String(row.email ?? ''),
    employeeId: String(row.employee_id ?? ''),
    gender: String(row.gender ?? ''),
    street: String(row.street ?? ''),
    city: String(row.city ?? ''),
    state: String(row.state ?? ''),
    zipcode: String(row.zipcode ?? ''),
    country: String(row.country ?? ''),
    birthdate: String(row.birthdate ?? ''),
    phoneNumber: String(row.phone_number ?? ''),
    onlineLink: String(row.online_link ?? ''),
});

const validationFieldToGridField: Record<string, string> = {
    first_name: 'firstName',
    middle_name: 'middleName',
    last_name: 'lastName',
    role: 'staffRole',
    email: 'email',
    employee_id: 'employeeId',
    gender: 'gender',
    street: 'streetAddress',
    city: 'city',
    state: 'state',
    zipcode: 'zip',
    country: 'country',
    birthdate: 'birthday',
    phone_number: 'primaryPhone',
    online_link: 'onlineLink',
};

const mapValidation = (value: unknown): AnalyzeResult['validation'] => {
    const validation = (value || {}) as ApiJson;
    const issues = Array.isArray(validation.issues) ? validation.issues : [];
    return {
        rowCount:
            typeof validation.row_count === 'number' ? validation.row_count : 0,
        validRowCount:
            typeof validation.valid_row_count === 'number'
                ? validation.valid_row_count
                : 0,
        invalidRowCount:
            typeof validation.invalid_row_count === 'number'
                ? validation.invalid_row_count
                : 0,
        issues: issues.map((value) => {
            const issue = value as ApiJson;
            return {
                rowNumber:
                    typeof issue.row_number === 'number' ? issue.row_number : 0,
                field:
                    validationFieldToGridField[String(issue.field ?? '')] ??
                    String(issue.field ?? ''),
                code: String(issue.code ?? ''),
                message: String(issue.message ?? ''),
                value:
                    typeof issue.value === 'string' || issue.value === null
                        ? issue.value
                        : undefined,
            };
        }),
    };
};

export type CreateImportJobResult = {
    jobId: string;
    uploadTarget: {
        url: string;
        contentType: string;
    };
    uploadReadyTarget: {
        url: string;
        contentType: string;
    };
};

export const createImportJob = async (
    fileName: string,
    options: { businessName: string },
): Promise<CreateImportJobResult> => {
    const baseUrl = getStaffImportApiUrl();
    const headers = await getAuthHeaders();
    const response = await fetch(`${baseUrl}/imports`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...headers,
        },
        body: JSON.stringify({
            business_name: options.businessName,
            import_type: 'staff',
            filename: fileName,
            content_type: 'text/csv',
        }),
    });
    const payload = await parseJsonResponse(response);
    const uploadTarget = payload.upload_target as ApiJson | undefined;
    if (!uploadTarget?.url) {
        throw new Error('Import API did not return an upload target.');
    }
    const uploadReadyTarget = payload.upload_ready_target as
        | ApiJson
        | undefined;
    if (!uploadReadyTarget?.url) {
        throw new Error('Import API did not return an upload ready target.');
    }
    return {
        jobId: String(payload.job_id),
        uploadTarget: {
            url: String(uploadTarget.url),
            contentType: String(uploadTarget.content_type ?? 'text/csv'),
        },
        uploadReadyTarget: {
            url: String(uploadReadyTarget.url),
            contentType: String(
                uploadReadyTarget.content_type ?? 'application/octet-stream',
            ),
        },
    };
};

export const uploadImportJobArtifacts = async (
    job: CreateImportJobResult,
    file: File,
    signal?: AbortSignal,
): Promise<void> => {
    await uploadToPresignedUrl(
        job.uploadTarget.url,
        file,
        job.uploadTarget.contentType,
        signal,
    );
    await uploadToPresignedUrl(
        job.uploadReadyTarget.url,
        new File([], 'ready'),
        job.uploadReadyTarget.contentType,
        signal,
    );
};

export const uploadToPresignedUrl = async (
    url: string,
    file: File,
    contentType: string,
    signal?: AbortSignal,
): Promise<void> => {
    const response = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: file,
        signal,
    });
    if (!response.ok) {
        throw new Error('Failed to upload CSV to storage.');
    }
};

export type StartWebCrawlResult = {
    jobId: string;
    importType: string;
};

export const startWebCrawl = async (
    url: string,
    options: { businessName: string },
): Promise<StartWebCrawlResult> => {
    const baseUrl = getStaffImportApiUrl();
    const headers = await getAuthHeaders();
    const response = await fetch(`${baseUrl}/imports/web-crawl`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...headers,
        },
        body: JSON.stringify({
            business_name: options.businessName,
            url,
        }),
    });
    const payload = await parseJsonResponse(response);
    return {
        jobId: String(payload.job_id),
        importType: String(payload.import_type ?? 'staff'),
    };
};

const mapGeneratedImportJobSummary = (
    job: ApiJson,
): GeneratedImportJobSummary => {
    const trigger = job.trigger as ApiJson | undefined;
    return {
        jobId: String(job.job_id),
        status: String(job.status),
        currentStage:
            typeof job.current_stage === 'string' ? job.current_stage : null,
        createdAt: String(job.created_at ?? ''),
        updatedAt: String(job.updated_at ?? ''),
        rowCount: typeof job.row_count === 'number' ? job.row_count : null,
        staffPageUrl:
            typeof job.staff_page_url === 'string' ? job.staff_page_url : null,
        trigger: trigger
            ? {
                  type: String(trigger.type ?? ''),
                  eventId: String(trigger.event_id ?? ''),
                  sourceUrl: String(trigger.source_url ?? ''),
              }
            : null,
    };
};

export const getLatestGeneratedImportJob = async (options: {
    businessName: string;
    signal?: AbortSignal;
}): Promise<LatestGeneratedImportJobResult> => {
    const baseUrl = getStaffImportApiUrl();
    const headers = await getAuthHeaders();
    const query = new URLSearchParams({
        business_name: options.businessName,
        import_type: 'staff',
        origin: 'website_discovery',
    });
    const response = await fetch(`${baseUrl}/imports/latest?${query}`, {
        headers,
        signal: options.signal,
    });
    const payload = await parseJsonResponse(response);
    const job = payload.job as ApiJson | null | undefined;
    if (!job) {
        return { job: null };
    }
    return {
        job: mapGeneratedImportJobSummary(job),
    };
};

export const pollImportJobStatus = async (
    jobId: string,
    options: {
        businessName: string;
        importType: string;
        signal?: AbortSignal;
    },
): Promise<ImportJobStatus> => {
    const baseUrl = getStaffImportApiUrl();
    const headers = await getAuthHeaders();
    const query = new URLSearchParams({
        business_name: options.businessName,
        importType: options.importType,
    });
    const response = await fetch(
        `${baseUrl}/imports/${encodeURIComponent(jobId)}/status?${query}`,
        { headers, signal: options.signal },
    );
    const payload = await parseJsonResponse(response);
    return {
        jobId: String(payload.job_id),
        status: String(payload.status),
        updatedAt:
            typeof payload.updated_at === 'string' ? payload.updated_at : null,
        rowCount:
            typeof payload.row_count === 'number' ? payload.row_count : null,
        errorCount:
            typeof payload.error_count === 'number'
                ? payload.error_count
                : null,
        error: typeof payload.error === 'string' ? payload.error : null,
    };
};

export const fetchImportJobData = async (
    jobId: string,
    options: {
        businessName: string;
        importType: string;
        signal?: AbortSignal;
    },
): Promise<AnalyzeResult> => {
    const baseUrl = getStaffImportApiUrl();
    const headers = await getAuthHeaders();
    const query = new URLSearchParams({
        business_name: options.businessName,
        importType: options.importType,
    });
    const response = await fetch(
        `${baseUrl}/imports/${encodeURIComponent(jobId)}/data?${query}`,
        { headers, signal: options.signal },
    );
    const payload = await parseJsonResponse(response);
    const rawRows = Array.isArray(payload.rows) ? payload.rows : [];
    return {
        jobId: String(payload.job_id),
        rows: rawRows.map((row) => mapEtlRowToStaffImportRow(row as ApiJson)),
        validation: mapValidation(payload.validation),
        existingBusinessEmails: Array.isArray(payload.existing_business_emails)
            ? payload.existing_business_emails.map((email) => String(email))
            : [],
    };
};

const POLL_INTERVAL_MS = 1500;
const MAX_POLL_ATTEMPTS = 120;

export const uploadCsv = async (
    file: File,
    options: { businessName: string },
): Promise<AnalyzeResult> => {
    const job = await createImportJob(file.name, {
        businessName: options.businessName,
    });
    await uploadImportJobArtifacts(job, file);

    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
        const status = await pollImportJobStatus(job.jobId, {
            businessName: options.businessName,
            importType: 'staff',
        });

        if (status.status === 'failed') {
            throw new Error(status.error || 'Import processing failed.');
        }

        if (status.status === 'ready_for_review') {
            return fetchImportJobData(job.jobId, {
                businessName: options.businessName,
                importType: 'staff',
            });
        }

        await new Promise((resolve) => {
            setTimeout(resolve, POLL_INTERVAL_MS);
        });
    }

    throw new Error('Import processing timed out.');
};

export const processStaffImport = async (options: {
    businessName: string;
    fileName: string;
    jobId: string;
    rows: import('../types/staffImport').StaffImportGridRow[];
}): Promise<ProcessResult> => {
    const baseUrl = getStaffImportApiUrl();
    const headers = await getAuthHeaders();
    const response = await fetch(`${baseUrl}/imports/staff`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...headers,
        },
        body: JSON.stringify({
            business_name: options.businessName,
            job_id: options.jobId,
            rows: options.rows.map(gridRowToProcessPayload),
        }),
    });
    const payload = await parseJsonResponse(response);
    const importedCount =
        typeof payload.imported_count === 'number' ? payload.imported_count : 0;
    return {
        status: String(payload.status) as ProcessResult['status'],
        importedCount,
        failedCount:
            typeof payload.failed_count === 'number' ? payload.failed_count : 0,
        fileName: options.fileName,
        results: Array.isArray(payload.results)
            ? payload.results.map((result) => {
                  const rowResult = result as ApiJson;
                  return {
                      rowId: String(rowResult.row_id),
                      status: String(
                          rowResult.status,
                      ) as ProcessResult['results'][number]['status'],
                      peopleId:
                          typeof rowResult.people_id === 'string'
                              ? rowResult.people_id
                              : null,
                      error:
                          typeof rowResult.error === 'string'
                              ? rowResult.error
                              : null,
                  };
              })
            : [],
    };
};

export const isStaffImportApiConfigured = (): boolean =>
    Boolean(getStaffImportApiUrl());
